import React, { useMemo, useState } from 'react'
import useGlobalStore from '@src/store/common/global'
import userStore from '@src/store/common/user'
import * as dateFns from 'date-fns'

/**
 * 全局水印
 * - 文本：当前登录用户名 + 页面渲染时间（渲染时冻结，后续不再更新）
 * - 仅在 store.watermarkEnabled === true 时渲染
 * - 颜色与字体大小写随当前主题（light/dark）自动适配
 * - 通过 SVG 平铺实现，pointer-events: none 不影响交互
 */
const GlobalWatermark: React.FC = () => {
	const enabled = useGlobalStore((s) => s.watermarkEnabled)
	// 使用已解析的主题（'system' 已被解析为 light/dark），保证视觉与实际渲染一致
	const resolvedTheme = useGlobalStore((s) => s.resolvedTheme)
	const userName = userStore((s) => s.nickname) || userStore.getState().name || 'Guest'

	// 渲染时一次性捕获时间戳，之后不再更新
	const [renderTime] = useState(() => dateFns.format(new Date(), 'yyyy-MM-dd HH:mm:ss'))

	// 浅色模式用淡灰、深色模式用更暗的灰，避免水印过亮或过暗
	const color = resolvedTheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'

	// 用 SVG data URL 平铺，每个水印单元包含 username · renderTime
	const bgImage = useMemo(() => {
		const text = `${userName} · ${renderTime}`
		const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160" viewBox="0 0 240 160">
  <g transform="rotate(-22 120 80)" fill="${color}" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif" font-size="14" font-weight="500">
    <text x="120" y="86" text-anchor="middle" dominant-baseline="middle">${text}</text>
  </g>
</svg>`.trim()
		return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`
	}, [userName, renderTime, color])

	if (!enabled) return null

	return (
		<div
			aria-hidden
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 2147483646,
				pointerEvents: 'none',
				backgroundImage: bgImage,
				backgroundRepeat: 'repeat',
				backgroundSize: '240px 160px'
			}}
		/>
	)
}

export default GlobalWatermark
